import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import BannerProcesoCompra from "/src/assets/images/proceso_compra.png";
import Banner1 from "/src/assets/images/banner40/aniversario-1-centros-chiloe.webp";
import Banner2 from "/src/assets/images/banner40/aniversario-2-fiordo.webp";
import Banner3 from "/src/assets/images/banner40/aniversario-3-ponton.webp";
import Banner4 from "/src/assets/images/banner40/aniversario-4-bandera.webp";
import Banner5 from "/src/assets/images/banner40/aniversario-5-oficinas.webp";
import { NOTICIAS_BANNER, USUARIOS_FINANZAS } from "../../utils/Constants";
import { InitDataContext } from "../../context/InitDataContext";
import {
  MenuLink,
  construirMenu,
  rutaDe,
} from "../../components/menu/menuTree.ts";
import { useScrollbarGutter } from "../../components/reportes/reportesUtils.ts";
import "../../utils/main.css";
import "../../screens/home/index.css";
import {
  ProcesoConteo,
  colorEstado,
  diasDesde,
  useInicioResumen,
} from "./inicioResumen.ts";
import "./inicio.css";

// 40 años: photos 2400x800 (3:1); the text goes over them as markup
const BANNERS = [
  { src: Banner1, alt: "Pontón junto a un centro de cultivo en Chiloé" },
  { src: Banner2, alt: "Centro de cultivo en un fiordo de la Patagonia" },
  { src: Banner3, alt: "Pontón con paneles solares entre jaulas de cultivo" },
  {
    src: Banner4,
    alt: "Bandera de Chile frente a montañas nevadas y un centro de cultivo",
  },
  { src: Banner5, alt: "Oficinas de Salmones de Chile" },
];

// Per-browser preferences (the page works without storage)
const CLAVE_BANNER_OCULTO = "fishweb-inicio-banner-oculto";
const CLAVE_NOVEDAD_VISTA = "fishweb-inicio-novedad-vista";
const leer = (clave: string) => {
  try {
    return localStorage.getItem(clave);
  } catch {
    return null;
  }
};
const guardar = (clave: string, valor: string) => {
  try {
    localStorage.setItem(clave, valor);
  } catch {
    /* storage unavailable: nothing to remember */
  }
};

// Quick access: menu codes in the order they are offered (max 9 tiles)
const PRIORIDAD_ACCESOS = [
  "SOCNVA",
  "SOCMAN",
  "SOCAPR",
  "SOCCONF",
  "OCNVA",
  "OCMAN",
  "OCAPR",
  "OCCONF",
  "MANFIN",
  "MANCONT",
  "PRESNVO",
  "VISGGMON",
];
const MAX_ACCESOS = 9;

// Accent per module group (same hues as the screens' headers)
const acentoDeGrupo = (grupo: string) => {
  const g = grupo.toLowerCase();
  if (g.includes("solicitud")) return "in-acento-soc";
  if (g.includes("orden")) return "in-acento-oc";
  if (g.includes("finanza")) return "in-acento-fin";
  if (g.includes("contab")) return "in-acento-cont";
  if (g.includes("presupuesto")) return "in-acento-ppto";
  if (g.includes("dashboard")) return "in-acento-rep";
  return "in-acento-oc";
};

// Purchase process: each step opens its screen when the user's menu has it
// "clave": the step's count in the summary (documents of the user there)
const PASOS = [
  {
    titulo: "Solicitud",
    clave: "solicitud" as keyof ProcesoConteo,
    texto: "Se crea la solicitud con sus líneas y centros de costo destino.",
    codigos: ["SOCNVA", "SOCMAN"],
    tono: "soc",
  },
  {
    titulo: "Aprobación",
    clave: "aprobacion" as keyof ProcesoConteo,
    texto: "La subgerencia responsable del presupuesto aprueba la solicitud.",
    codigos: ["SOCAPR"],
    tono: "soc",
  },
  {
    titulo: "Confirmación",
    clave: "confirmacion" as keyof ProcesoConteo,
    texto: "El departamento de Adquisiciones confirma la solicitud.",
    codigos: ["SOCCONF"],
    tono: "soc",
  },
  {
    titulo: "Orden de compra",
    clave: "ordenCompra" as keyof ProcesoConteo,
    texto: "Se emite la orden de compra y sigue su flujo de aprobación.",
    codigos: ["OCMAN", "OCNVA"],
    tono: "oc",
  },
  {
    titulo: "Recepción",
    clave: "recepcion" as keyof ProcesoConteo,
    texto: "Se reciben los bienes o servicios de la orden.",
    codigos: [],
    tono: "oc",
  },
  {
    titulo: "Factura",
    clave: "factura" as keyof ProcesoConteo,
    texto: "Finanzas asocia las facturas y revisa su cuadratura con la OC.",
    codigos: ["MANFIN", "MANCONT"],
    tono: "fin",
  },
  {
    titulo: "Pago",
    clave: "pago" as keyof ProcesoConteo,
    texto: "Se paga la factura y la orden queda cumplida.",
    codigos: ["MANFIN"],
    tono: "fin",
  },
];

// "Pendiente para mí": one row per action the user's menu allows
const PENDIENTES = [
  {
    clave: "solicitudesPorAprobar",
    titulo: "Solicitudes por aprobar",
    codigo: "SOCAPR",
  },
  {
    clave: "solicitudesPorConfirmar",
    titulo: "Solicitudes por confirmar",
    codigo: "SOCCONF",
  },
  {
    clave: "ordenesPorAprobar",
    titulo: "Órdenes por aprobar",
    codigo: "OCAPR",
  },
  {
    clave: "ordenesPorConfirmar",
    titulo: "Órdenes por confirmar",
    codigo: "OCCONF",
  },
] as const;

const clp = (v: number) =>
  `$ ${Math.round(Number(v) || 0).toLocaleString("es-CL")}`;

const fechaCorta = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString("es-CL", { day: "2-digit", month: "short" });
};

const textoDias = (dias: number) => `${dias} ${dias === 1 ? "día" : "días"}`;

const saludo = (hora: number) =>
  hora < 12 ? "Buenos días" : hora < 20 ? "Buenas tardes" : "Buenas noches";

function IndexBoxHome() {
  useScrollbarGutter();
  const { initData, menus } = useContext(InitDataContext);

  /* ---------------- Menu-derived links (permissions from the DB) -------- */
  const arbol = useMemo(() => construirMenu(menus), [menus]);
  const enlacesPorCodigo = useMemo(() => {
    const mapa = new Map<string, { link: MenuLink; grupo: string }>();
    arbol.grupos.forEach((g) =>
      g.links.forEach((l) => {
        if (l.codigo && !mapa.has(l.codigo))
          mapa.set(l.codigo, { link: l, grupo: g.nombre });
      }),
    );
    return mapa;
  }, [arbol]);

  const accesos = useMemo(() => {
    const lista = PRIORIDAD_ACCESOS.map((c) => enlacesPorCodigo.get(c)).filter(
      Boolean,
    ) as { link: MenuLink; grupo: string }[];
    // Finanzas users who can mark orders CUMPLIDA / SEMI CUMPLIDA: first
    // tile, opening the follow-up screen on its candidates (EMITIDA orders
    // with a paid invoice; same URL presets as the finanzas dashboard)
    const seguimientoFinanzas = enlacesPorCodigo.get("MANFIN")?.link;
    if (seguimientoFinanzas && USUARIOS_FINANZAS.includes(initData?.id)) {
      lista.unshift({
        link: {
          ...seguimientoFinanzas,
          key: "acceso-marcar-cumplidas",
          codigo: "MARCAR_CUMPLIDA",
          nombre: "Marcar OC cumplidas",
          url: `${rutaDe(seguimientoFinanzas.url)}?estadoOrden=2&estadoPago=PAGADA`,
          icono: "pi pi-check-circle",
        },
        grupo: "Finanzas · emitidas con factura pagada",
      });
    }
    // Reportes always keeps a visible slot (the last one when the list is full)
    const primerReporte = arbol.reportes?.links[0];
    if (primerReporte)
      lista.splice(Math.min(lista.length, MAX_ACCESOS - 1), 0, {
        link: {
          ...primerReporte,
          nombre: "Reportes",
          icono: "pi pi-chart-bar",
        },
        grupo: "Dashboards",
      });
    return lista.slice(0, MAX_ACCESOS);
  }, [enlacesPorCodigo, arbol, initData?.id]);

  const nuevaSolicitud = enlacesPorCodigo.get("SOCNVA")?.link;

  /* ---------------- Greeting ---------------- */
  const ahora = new Date();
  const fecha = ahora.toLocaleDateString("es-CL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const primerNombre = String(initData?.nombres ?? "")
    .trim()
    .split(/\s+/)[0];
  const subrogancia =
    !!initData?.mi_subrogancia &&
    String(initData.mi_subrogancia).trim().length > 0;

  /* ---------------- Banner ---------------- */
  const [bannerOculto, setBannerOculto] = useState(
    () => leer(CLAVE_BANNER_OCULTO) === "1",
  );
  const [bannerActual, setBannerActual] = useState(0);
  useEffect(() => {
    if (bannerOculto || BANNERS.length < 2) return;
    const reducir =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducir) return;
    const id = window.setInterval(
      () => setBannerActual((i) => (i + 1) % BANNERS.length),
      8000,
    );
    return () => window.clearInterval(id);
  }, [bannerOculto]);
  const ocultarBanner = () => {
    guardar(CLAVE_BANNER_OCULTO, "1");
    setBannerOculto(true);
  };
  const mostrarBanner = () => {
    guardar(CLAVE_BANNER_OCULTO, "0");
    setBannerOculto(false);
  };

  /* ---------------- Novedades ---------------- */
  // "NUEVO": releases published after the last one this browser saw
  const [ultimaVista] = useState(() => leer(CLAVE_NOVEDAD_VISTA));
  const indiceVista = NOTICIAS_BANNER.findIndex(
    (n) => n.version === ultimaVista,
  );
  const esNueva = (i: number) =>
    indiceVista === -1 ? i === 0 : i < indiceVista;
  useEffect(() => {
    if (NOTICIAS_BANNER[0]?.version)
      guardar(CLAVE_NOVEDAD_VISTA, NOTICIAS_BANNER[0].version);
  }, []);

  const [verHistorial, setVerHistorial] = useState(false);
  const [verDiagrama, setVerDiagrama] = useState(false);

  /* ---------------- Mi trabajo (GET /reports/inicio/resumen) ---------- */
  const resumen = useInicioResumen(initData);
  const datos = resumen.estado === "ok" ? resumen.datos : null;
  const filasPendientes = PENDIENTES.map((f) => ({
    ...f,
    link: enlacesPorCodigo.get(f.codigo)?.link,
  })).filter((f) => f.link);
  const mostrarPendientes =
    resumen.estado !== "error" && filasPendientes.length > 0;
  const rutaMantencionSoc = enlacesPorCodigo.get("SOCMAN")?.link.url;
  const rutaMantencionOc = enlacesPorCodigo.get("OCMAN")?.link.url;

  return (
    <div className="in-page">
      {/* ---------------- Greeting ---------------- */}
      <header className="in-saludo">
        <div className="in-saludo-texto">
          <span className="in-fecha">{fecha}</span>
          <h1 className="in-titulo">
            {saludo(ahora.getHours())}
            {primerNombre ? `, ${primerNombre}` : ""}
          </h1>
          <div className="in-chips">
            {initData?.centro_costo ? (
              <span className="in-chip">
                <b>Centro Costo: </b>
                {initData.centro_costo}
              </span>
            ) : null}
            {initData?.area_nombre || initData?.sucursal_nombre ? (
              <span className="in-chip">
                <b>Área: </b>
                {[initData?.area_nombre, initData?.sucursal_nombre]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            ) : null}
            {subrogancia ? (
              <span
                className="in-chip in-chip-aviso"
                title="Estás subrogando a otro usuario: verás también sus documentos pendientes"
              >
                <i className="pi pi-user-plus" aria-hidden="true" /> Subrogancia
                activa
              </span>
            ) : null}
          </div>
        </div>
        {nuevaSolicitud ? (
          <Link to={nuevaSolicitud.url} className="in-btn-primario">
            <i className="pi pi-plus" aria-hidden="true" /> Nueva solicitud
          </Link>
        ) : null}
      </header>

      {/* ---------------- Banner (full width, whole image visible) -------- */}
      {bannerOculto ? (
        <button
          type="button"
          className="in-banner-mostrar"
          onClick={mostrarBanner}
        >
          <i className="pi pi-image" aria-hidden="true" /> Mostrar banner 40
          años
        </button>
      ) : (
        <figure className="in-banner" aria-label="40 años Salmones de Chile">
          {BANNERS.map((b, i) => (
            <img
              key={b.alt}
              className={
                i === bannerActual ? "in-banner-img activo" : "in-banner-img"
              }
              src={b.src}
              alt={i === bannerActual ? b.alt : ""}
              aria-hidden={i === bannerActual ? undefined : true}
              loading={i === 0 ? "eager" : "lazy"}
            />
          ))}
          <figcaption className="in-banner-texto">
            <span className="in-banner-celebra">
              ¡CELEBRAMOS NUESTROS 40 AÑOS!
            </span>
            <span className="in-banner-empresa">
              SALMONES DE CHILE Y PRODUCTORA SA
            </span>
            <span className="in-banner-lema">
              4 décadas de excelencia y compromiso
            </span>
            <span className="in-banner-cita">
              ‘Chiloé nos vió nacer, el mar nos hizo crecer’
            </span>
          </figcaption>
          <button
            type="button"
            className="in-banner-cerrar"
            onClick={ocultarBanner}
            aria-label="Ocultar banner"
            title="Ocultar banner"
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
          {BANNERS.length > 1 ? (
            <div className="in-banner-puntos">
              {BANNERS.map((b, i) => (
                <button
                  key={b.alt}
                  type="button"
                  className={`in-banner-punto${i === bannerActual ? " activo" : ""}`}
                  onClick={() => setBannerActual(i)}
                  aria-label={`Ver banner ${i + 1}`}
                  aria-pressed={i === bannerActual}
                />
              ))}
            </div>
          ) : null}
        </figure>
      )}

      <div
        className={
          mostrarPendientes && accesos.length ? "in-fila in-fila-2" : "in-fila"
        }
      >
        {/* ---------------- Pendiente para mí ---------------- */}
        {mostrarPendientes ? (
          <section className="in-card" aria-labelledby="in-pend-t">
            <div className="in-card-head">
              <h2 id="in-pend-t">Pendiente para mí</h2>
              <span className="in-card-sub">
                Documentos que requieren tu acción
              </span>
            </div>
            <div className="in-pendientes">
              {filasPendientes.map((f) => {
                const dato = datos?.pendientes?.[f.clave];
                const cantidad = Number(dato?.cantidad) || 0;
                const dias = diasDesde(dato?.masAntigua);
                const sub = !datos
                  ? "Consultando..."
                  : cantidad && dias !== null
                    ? "La más antigua: hace " + textoDias(dias)
                    : cantidad
                      ? "Requiere tu revisión"
                      : "Nada pendiente";
                return (
                  <Link
                    key={f.clave}
                    to={f.link!.url}
                    className={
                      cantidad ? "in-pendiente con-pendientes" : "in-pendiente"
                    }
                  >
                    <span className="in-pendiente-texto">
                      <span className="in-pendiente-titulo">{f.titulo}</span>
                      <span className="in-pendiente-sub">{sub}</span>
                    </span>
                    <span className="in-pendiente-accion">
                      {datos ? (
                        <span className="in-contador">{cantidad}</span>
                      ) : (
                        <span className="in-contador in-contador-cargando" />
                      )}
                      <span className="in-pendiente-ir">
                        {cantidad ? "Revisar" : "Ver"}
                        <i className="pi pi-arrow-right" aria-hidden="true" />
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* ---------------- Quick access (from the user's DB menu) ---------- */}
        {accesos.length ? (
          <section className="in-card" aria-labelledby="in-accesos-t">
            <div className="in-card-head">
              <h2 id="in-accesos-t">Accesos rápidos</h2>
              <span className="in-card-sub">
                Según los permisos de tu perfil
              </span>
            </div>
            <div className="in-accesos">
              {accesos.map(({ link, grupo }) => (
                <Link
                  key={link.key}
                  to={link.url}
                  className={`in-acceso ${acentoDeGrupo(grupo)}`}
                >
                  <span className="in-acceso-icono" aria-hidden="true">
                    <i className={link.icono ?? "pi pi-angle-right"} />
                  </span>
                  <span className="in-acceso-texto">
                    <span className="in-acceso-nombre">{link.nombre}</span>
                    <span className="in-acceso-grupo">{grupo}</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </div>

      {/* ---------------- Mis documentos recientes ---------------- */}
      {resumen.estado !== "error" ? (
        <section className="in-card" aria-labelledby="in-rec-t">
          <div className="in-card-head">
            <h2 id="in-rec-t">Mis documentos recientes</h2>
            <span className="in-card-sub">
              Tus últimas solicitudes y órdenes de compra
            </span>
          </div>
          {!datos ? (
            <div className="in-recientes-cargando" aria-label="Cargando">
              {[0, 1, 2].map((i) => (
                <span key={i} className="in-esqueleto" />
              ))}
            </div>
          ) : datos.recientes.length === 0 ? (
            <p className="in-vacio">Aún no tienes documentos registrados.</p>
          ) : (
            <div className="in-tabla-scroll">
              <table className="in-tabla">
                <thead>
                  <tr>
                    <th scope="col">Documento</th>
                    <th scope="col">Centro de costo destino</th>
                    <th scope="col" className="in-num">
                      Monto
                    </th>
                    <th scope="col">Estado</th>
                    <th scope="col">Actualizado</th>
                    <th scope="col">
                      <span className="in-sr">Abrir</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {datos.recientes.slice(0, 5).map((d) => {
                    const ruta =
                      d.tipo === "SOC" ? rutaMantencionSoc : rutaMantencionOc;
                    const centros = d.centrosCostoDestino ?? [];
                    return (
                      <tr key={d.tipo + "-" + d.numero}>
                        <td className="in-doc">
                          {d.tipo} {d.numero}
                        </td>
                        <td title={centros.join(" | ")}>
                          {centros[0] ?? "-"}
                          {centros.length > 1
                            ? " (+" + (centros.length - 1) + ")"
                            : ""}
                        </td>
                        <td className="in-num">{clp(d.montoClp)}</td>
                        <td>
                          <span
                            className="in-estado"
                            style={{
                              backgroundColor: colorEstado(d.estado?.codigo),
                            }}
                          >
                            {d.estado?.nombre ?? "-"}
                          </span>
                        </td>
                        <td>{fechaCorta(d.fecha)}</td>
                        <td className="in-num">
                          {ruta ? (
                            <Link
                              to={ruta}
                              className="in-link-tabla"
                              title={
                                "Buscar " +
                                d.tipo +
                                " " +
                                d.numero +
                                " en seguimiento"
                              }
                            >
                              Seguimiento
                              <i
                                className="pi pi-arrow-right"
                                aria-hidden="true"
                              />
                            </Link>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {/* ---------------- Purchase process ---------------- */}
      <section className="in-card" aria-labelledby="in-proceso-t">
        <div className="in-card-head">
          <h2 id="in-proceso-t">Proceso de compra</h2>
          <button
            type="button"
            className="in-link"
            onClick={() => setVerDiagrama(true)}
          >
            <i className="pi pi-sitemap" aria-hidden="true" /> Ver diagrama
            completo
          </button>
        </div>
        <ol className="in-pasos">
          {PASOS.map((p, i) => {
            const destino = p.codigos
              .map((c) => enlacesPorCodigo.get(c)?.link)
              .find(Boolean);
            const contenido = (
              <>
                <span className="in-paso-num">Paso {i + 1}</span>
                <span className="in-paso-titulo">{p.titulo}</span>
                <span className="in-paso-texto">{p.texto}</span>
                {datos ? (
                  <span className="in-paso-conteo">
                    <strong>{Number(datos.proceso?.[p.clave]) || 0}</strong>
                    {p.clave === "solicitud"
                      ? " creadas este mes"
                      : " tuyos en esta etapa"}
                  </span>
                ) : null}
                {destino ? (
                  <span className="in-paso-ir">
                    Abrir <i className="pi pi-arrow-right" aria-hidden="true" />
                  </span>
                ) : null}
              </>
            );
            return (
              <li key={p.titulo} className={`in-paso in-paso-${p.tono}`}>
                {destino ? (
                  <Link
                    to={destino.url}
                    className="in-paso-caja"
                    title={`Ir a ${destino.nombre}`}
                    aria-current={
                      rutaDe(destino.url) === window.location.pathname
                        ? "page"
                        : undefined
                    }
                  >
                    {contenido}
                  </Link>
                ) : (
                  <div className="in-paso-caja in-paso-info">{contenido}</div>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      {/* ---------------- FishWeb releases ---------------- */}
      <section className="in-card" aria-labelledby="in-novedades-t">
        <div className="in-card-head">
          <div>
            <h2 id="in-novedades-t">Novedades de FishWeb</h2>
            <span className="in-card-sub">
              Nuevas versiones, funcionalidades y mejoras de la aplicación
            </span>
          </div>
          <button
            type="button"
            className="in-link"
            onClick={() => setVerHistorial(true)}
          >
            Ver historial de versiones
          </button>
        </div>
        <div className="in-novedades">
          {NOTICIAS_BANNER.slice(0, 3).map((n, i) => (
            <article key={`${n.version}-${i}`} className="in-novedad">
              <div className="in-novedad-meta">
                {esNueva(i) ? (
                  <span className="in-badge-nuevo">NUEVO</span>
                ) : null}
                <span>
                  {n.version} · {n.date}
                </span>
              </div>
              <span className="in-novedad-cat">{n.category}</span>
              <h3 className="in-novedad-titulo">{n.title}</h3>
              <p className="in-novedad-texto">{n.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ---------------- Dialogs ---------------- */}
      <Dialog
        header="Historial de versiones de FishWeb"
        visible={verHistorial}
        onHide={() => setVerHistorial(false)}
        style={{ width: "min(760px, 94vw)" }}
        modal
        dismissableMask
      >
        <ol className="in-historial">
          {NOTICIAS_BANNER.map((n, i) => (
            <li key={`h-${n.version}-${i}`}>
              <span className="in-historial-version">{n.version}</span>
              <div>
                <strong>{n.title}</strong>
                <span className="in-historial-meta">
                  {n.category} · {n.date}
                </span>
                <p className="in-novedad-texto">{n.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </Dialog>

      <Dialog
        header="Diagrama del proceso de compra"
        visible={verDiagrama}
        onHide={() => setVerDiagrama(false)}
        style={{ width: "min(1100px, 96vw)" }}
        modal
        dismissableMask
      >
        <img
          src={BannerProcesoCompra}
          alt="Diagrama del proceso de compra"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      </Dialog>
    </div>
  );
}

export default IndexBoxHome;
